import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Alert, Button, IconButton, Tooltip, Grid, CircularProgress, MenuItem, Select } from '@mui/material';

import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import PlusIcon from '@mui/icons-material/AddShoppingCart';
import MinusIcon from '@mui/icons-material/RemoveShoppingCart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { showValidationAlert } from 'helper/form';
import { EBookingMode } from 'types';
import { bookingModeSelector } from 'helper/filter';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputDropdown, SimpleDropdown, buildMenuItemsFromOptions } from 'components/form/FormInputDropdown';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { TaxComponentSelectionInput } from 'components/tax/TaxComponentSelectionInput';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { SpaceSelectionInput, buildSpaceSelectionOptions } from 'components/security/SpaceSelectionInput';
import { buildGroupedSpaceSelectionOptions } from 'components/security/GroupedSpaceSelectionInput';
import { userSelector, canEditAdminRecord, canEditAdminSpaceId, initialSpace, filterForSelectableRecords } from 'helper/security';
import { filterSelector } from 'helper/filter';

import ProductDetailsFlat, {
  ProductDetailsFlatContentSchema,
  ProductDetailsFlatFromTypeToSchema,
  ProductDetailsFlatFromSchemaToType,
} from '../../content/products/productdetailsflat_embedded';
import PriceTableView from '../pricetable_embedded';
import { AvailabilityEmbeddedTable } from '../../availability/availability';

import {
  PRODUCTBUNDLE_VIEW_QUERY,
  PRODUCTS_LIST_QUERY,
  UPDATE_PRODUCTBUNDLE_MUTATION,
  CREATE_PRODUCTBUNDLE_MUTATION,
  DELETE_PRODUCTBUNDLE_MUTATION,
  SET_PRODUCTDETAILS_MUTATION,
  COPY_PRODUCTBUNDLE_MUTATION,
  REFETCH_PRODUCTBUNDLES_QUERIES,
  EVICT_PRODUCTBUNDLES_QUERIES,
} from '../gql';
import { TAXTYPES_LIST_FULL_QUERY } from '../../prices/gql';
import {
  TaxTypeListOutput,
  ProductListOutput,
  ProductBundleListOutput,
  EProductPayment,
  EProductLookupCode,
  ProductBundleComponentListOutput,
  SpaceShortFragment,
} from '__generated__/graphql';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { isProductionRelease } from 'helper/deployment';
import { ISpace } from 'types';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { canEditProductsRecord, canEditProductsTextRecord } from '../helper';
import LocalizedSkuText from '../LocalizedSkuText';

interface ProductBundleProps {
  id: number;
}
interface ProductBundleCreateProps { }
interface ProductBundleFormProps {
  data: ProductBundleListOutput;
  products: ProductListOutput[];
  productsLoading: boolean;
  taxTypesData: TaxTypeListOutput[];
}

const bundleItemsSchema = yup.object().shape({
  includedCount: yup.number().min(0).required().label(i18next.t('productbundle-item-includedcount')),
  isOptional: yup.boolean().required(),
  productId: yup.number().required(),
});

const validationSchema = yup.object().shape({
  isCreate: yup.boolean().required(),
  isSystem: yup.boolean().required(),
  name: yup.string().required().label(i18next.t('productbundle-name')),
  extRefCode: yup.string().nullable(),
  spaceId: yup.number().required().label(i18next.t('field-space')),
  sku: yup.string(),
  recurring: yup.string().required(),
  lookupCode: yup.string().nullable(),
  components: yup.array().required().min(1).of(yup.number().required()).label(i18next.t('productbundle-components')),
  bundleItems: yup.array().required().of(bundleItemsSchema),
  content: yup.array().required().of(ProductDetailsFlatContentSchema),
});

function ProductBundleForm(props: ProductBundleFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const filter = filterSelector();

  const [updateMutateFunction] = useMutation(UPDATE_PRODUCTBUNDLE_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_PRODUCTBUNDLE_MUTATION);
  const [setTextMutateFunction] = useMutation(SET_PRODUCTDETAILS_MUTATION);

  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditProductsRecord(user, props.data);
  const canEditText = props.data.id < 0 || canEditProductsTextRecord(user, props.data);

  const [createdId, setCreatedId] = useState(0);
  useEffect(() => {
    if (createdId > 0) navigate(`/products/bundles/${createdId}`);
  }, [createdId]);

  const [selectedSpace, setSelectedSpace] = useState<ISpace | null>(props.data.space);

  // Performance: Create lookup map for bundle items to avoid O(n²) in render
  const bundleItemsMap = React.useMemo(
    () => new Map(props.data.bundleItems.map(bi => [bi.product.id, bi.product])),
    [props.data.bundleItems]
  );

  type ProductBundleFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: ProductBundleListOutput): ProductBundleFormType => ({
    ...obj,
    isCreate: props.data.id > 0 ? false : true,
    spaceId: obj.space.id,
    lookupCode: obj.lookupCode || undefined,
    components: obj.components.map(tt => tt.taxType.id),
    bundleItems: obj.bundleItems.map(bi => ({
      isOptional: bi.isOptional,
      includedCount: bi.includedCount,
      productId: bi.product.id,
    })),
    content: obj.content.map(ProductDetailsFlatFromTypeToSchema),
    extRefCode: [obj.extRefCode, obj.extPricelistRefCode].filter(r => r).join(' / '),
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    getValues,
    watch,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient(), products: props.products },
    defaultValues: toFormSchema((props.data || {}) as ProductBundleListOutput),
  });

  const {
    fields: bundleItemsField,
    append: bundleItemAppend,
    remove: bundleItemRemove,
  } = useFieldArray({
    control,
    name: 'bundleItems',
  });
  const {
    fields: contentFields,
    append: contentAppend,
    remove: contentRemove,
  } = useFieldArray({
    control,
    name: 'content',
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit = async (values: ProductBundleFormType) => {
    const editableContent = values.content.map(ProductDetailsFlatFromSchemaToType).filter(t => canEditAdminSpaceId(user, t.spaceId));
    try {
      if (props.data.id > 0) {
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.name,
              isSystem: !!values.isSystem,
              recurring: values.recurring as EProductPayment,
              lookupCode: values.lookupCode ? (values.lookupCode as EProductLookupCode) : null,
              bundleItems: values.bundleItems.map(bi => ({
                productId: bi.productId,
                includedCount: bi.includedCount,
                isOptional: !!bi.isOptional,
              })),
              components: values.components.map(c => ({ taxTypeId: c })),
              ...(canEditText && editableContent.length > 0 ? { content: editableContent } : {}),
            },
          },
          update: cache => EVICT_PRODUCTBUNDLES_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRODUCTBUNDLES_QUERIES(props.data.id, values.sku),
        });
        reset(toFormSchema((res.data!.updateProductBundle || {}) as ProductBundleListOutput));
      } else {
        const res = await createMutateFunction({
          variables: {
            spaceId: values.spaceId,
            data: {
              name: values.name,
              isSystem: !!values.isSystem,
              forLister: props.data.forLister,
              lookupCode: values.lookupCode ? (values.lookupCode as EProductLookupCode) : null,
              recurring: values.recurring as EProductPayment,
              bundleItems: values.bundleItems.map(bi => ({
                productId: bi.productId,
                includedCount: bi.includedCount,
                isOptional: !!bi.isOptional,
              })),
              components: values.components.map(c => ({ taxTypeId: c })),
              content: values.content.map(ProductDetailsFlatFromSchemaToType),
            },
          },
          update: cache => EVICT_PRODUCTBUNDLES_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_PRODUCTBUNDLES_QUERIES(),
        });
        reset(toFormSchema((res.data!.createProductBundle || {}) as ProductBundleListOutput));
        setCreatedId(res.data!.createProductBundle.id);
      }
      dispatchMessage(dispatch, i18next.t('productbundle-saved'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };
  const onSubmitText = async (values: ProductBundleFormType) => {
    if (props.data.id <= 0 || !canEditText) return;
    try {
      await setTextMutateFunction({
        variables: {
          spaceId: props.data.space.id,
          sku: props.data.sku,
          content: values.content.map(ProductDetailsFlatFromSchemaToType).filter(t => canEditAdminSpaceId(user, t.spaceId)),
        },
        update: cache => EVICT_PRODUCTBUNDLES_QUERIES(cache),
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_PRODUCTBUNDLES_QUERIES(props.data.id, values.sku),
      });
      let bundle: ProductBundleListOutput | undefined;
      try {
        const cached = client.readQuery<{ viewProductBundle: ProductBundleListOutput }>({
          query: PRODUCTBUNDLE_VIEW_QUERY,
          variables: { id: props.data.id },
        });
        bundle = cached?.viewProductBundle;
      } catch {
        /* readQuery can throw on cache miss */
      }
      if (!bundle) {
        const { data } = await client.query<{ viewProductBundle: ProductBundleListOutput }>({
          query: PRODUCTBUNDLE_VIEW_QUERY,
          variables: { id: props.data.id },
          fetchPolicy: 'network-only',
        });
        bundle = data?.viewProductBundle;
      }
      if (bundle) {
        reset(toFormSchema(bundle));
      }
      dispatchMessage(dispatch, i18next.t('productbundle-saved'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const initialTabSelected = (location.hash === '#texts' && 2) || (location.hash === '#prices' && 1) || 0;

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t('productbundles-list-page-title'), props.data])}</title>
      </Helmet>
      <Grid container spacing={3}>
        <UnsavedChangesPrompt isDirty={isDirty} />
        <Grid item xs={12} sm={6}>
          <FormInputText name="name" control={control} label={i18next.t('productbundle-name')} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SpaceSelectionInput
            checkAdmin
            useFilterSpaceForRoot
            name="spaceId"
            control={control}
            disabled={!canEdit || props.data.id > 0 || user.isSingleAdminSpace}
            onSpaceSelected={space => setSelectedSpace(space)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputDropdown
            name="recurring"
            control={control}
            label={i18next.t('productbundle-recurring')}
            options={Object.keys(EProductPayment).map(p => ({
              value: p,
              label: `${i18next.t('enums-EProductPayment-' + p)}`,
            }))}
            required
            disabled={!canEdit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputText name="sku" control={control} label={i18next.t('productbundle-sku')} disabled />
        </Grid>
        <Grid item xs={12} sm={6}></Grid>
        <Grid item xs={12} sm={3}>
          <FormInputDropdown
            name="lookupCode"
            control={control}
            label={i18next.t('productbundle-lookupcode')}
            options={[
              {
                label: <em>{i18next.t('generic-selection-empty')}</em>,
                value: undefined,
              },
              ...Object.keys(EProductLookupCode).map(p => ({
                value: p,
                label: `${p}`,
              })),
            ]}
            disabled={!canEdit}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormInputText name="extRefCode" control={control} label={i18next.t('productbundle-extrefcode')} disabled />
        </Grid>
        <Grid item xs={12}>
          {user.isSeminargo && (<FormInputCheckbox name="isSystem" control={control} label={i18next.t('productbundle-issystem')} disabled />)}
        </Grid>
        <Grid item xs={12}>
          {(user.isSeminargo || !props.data.isSystem) && (
            <TaxComponentSelectionInput
              name="components"
              control={control}
              label={i18next.t('productbundle-components')}
              country={selectedSpace ? selectedSpace.country : null}
              taxTypesData={props.taxTypesData}
              disabled={!canEdit}
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <CustomTabs
            initialSelected={initialTabSelected}
            headers={[
              i18next.t('productbundle-items-tab'),
              i18next.t('productbundle-prices-tab'),
              i18next.t('productbundle-text-tab'),
              i18next.t('productbundle-availability-tab'),
            ]}
            icons={[
              validationErrors.bundleItems ? <ErrorIcon /> : undefined,
              undefined,
              validationErrors.content ? <ErrorIcon /> : undefined,
              undefined,
            ]}
            hidden={[false, false, false, isProductionRelease() || !user.isSeminargo]}
            disabled={props.data.id > 0 ? [false, false, false, false] : [false, true, true, true]}
            tabs={[
              <>
                <SimpleTable
                  headers={[i18next.t('productbundle-item-product'), '', '', '']}
                  rows={bundleItemsField.map((field, index) => {
                    // Use pre-built map for O(1) lookup instead of O(n) find
                    const product = bundleItemsMap.get(field.productId) || props.products.find(p => p.id === field.productId);

                    // Handle missing product gracefully
                    if (!product) {
                      return [
                        <Alert severity="warning" sx={{ p: 0.5 }}>
                          {i18next.t('productbundle-item-product-not-found', { id: field.productId })} (ID: {field.productId})
                        </Alert>,
                        <FormInputNumber
                          key={`${field.id}.includedCount`}
                          name={`bundleItems.${index}.includedCount`}
                          control={control}
                          label={i18next.t('productbundle-item-includedcount')}
                          textFieldProps={{ fullWidth: false }}
                          required
                          disabled={true}
                        />,
                        <FormInputCheckbox
                          key={`${field.id}.isOptional`}
                          name={`bundleItems.${index}.isOptional`}
                          control={control}
                          label={i18next.t('productbundle-item-isoptional')}
                          disabled={true}
                        />,
                        <PlusIcon />,
                        canEdit && (
                          <Tooltip title={i18next.t('productbundle-item-remove-missing') || ''}>
                            <IconButton onClick={() => bundleItemRemove(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        ),
                      ];
                    }

                    return [
                      <Link to={`/products/products/${field.productId}`}>
                        <LocalizedSkuText
                          sku={product.sku}
                          fallback={product.name}
                          hotelId={filter?.hotelId || null}
                          spaceId={watch('spaceId')}
                        />
                        {product.extRefCode && ` (${product.extRefCode})`}
                      </Link>,
                      <FormInputNumber
                        key={`${field.id}.includedCount`}
                        name={`bundleItems.${index}.includedCount`}
                        control={control}
                        label={i18next.t('productbundle-item-includedcount')}
                        textFieldProps={{ fullWidth: false }}
                        required
                        disabled={!canEdit}
                      />,
                      <FormInputCheckbox
                        key={`${field.id}.isOptional`}
                        name={`bundleItems.${index}.isOptional`}
                        control={control}
                        label={i18next.t('productbundle-item-isoptional')}
                        disabled={!canEdit}
                      />,
                      product.isDeduction ? <MinusIcon /> : <PlusIcon />,
                      canEdit && (
                        <IconButton onClick={() => bundleItemRemove(index)}>
                          <DeleteIcon />
                        </IconButton>
                      ),
                    ];
                  })}
                />
                {canEdit && (
                  <Select
                    value={0}
                    displayEmpty
                    disabled={props.productsLoading}
                    onChange={event =>
                      event.target.value &&
                      bundleItemAppend({
                        includedCount: 1,
                        isOptional: false,
                        productId: event.target.value as number,
                      })
                    }
                  >
                    {props.productsLoading ? (
                      <MenuItem value={0}>
                        <em>{i18next.t('productbundle-item-add')}...</em>
                      </MenuItem>
                    ) : (
                      buildMenuItemsFromOptions(
                        buildGroupedSpaceSelectionOptions(
                          user,
                          filterForSelectableRecords(
                            user,
                            props.products.filter(p => getValues('bundleItems').findIndex(bi => bi.productId === p.id) < 0 && p.forLister === props.data.forLister),
                            getValues('spaceId'),
                            false,
                            false,
                          ),
                          `${i18next.t('productbundle-item-add')}`,
                          0,
                        ),
                      )
                    )}
                  </Select>
                )}
              </>,
              <PriceTableView prices={props.data.prices} sku={props.data.sku} />,
              <ProductDetailsFlat
                control={control}
                baseSpaceId={watch('spaceId')}
                fields={contentFields}
                append={contentAppend}
                remove={contentRemove}
                watch={(index, field) => watch(`content.${index}.${field}`)}
                validationErrors={validationErrors}
              />,
              props.data.id > 0 && !isProductionRelease() ? <AvailabilityEmbeddedTable bundleId={props.data.id} /> : null,
            ]}
          />
        </Grid>
        {canEdit && (
          <Grid item xs={12}>
            {showValidationAlert(validationErrors)}
          </Grid>
        )}
        <Grid item xs={12}>
          {canEdit && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('productbundle-save')}
            </Button>
          )}
          {props.data.id > 0 && !canEdit && canEditText && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || isSubmitting || isValidating}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmitText)();
                }
              }}
            >
              {i18next.t('productbundle-save-text')}
            </Button>
          )}
          {canEdit && props.data.id > 0 && (
            <ProductBundleDeleteButton id={props.data.id} space={props.data.space} isSystem={props.data.isSystem} forLister={props.data.forLister} icon={false} />
          )}
          {props.data.id > 0 && <ProductBundleCopyButton id={props.data.id} spaceId={props.data.space.id} icon={false} />}
        </Grid>
      </Grid>
    </>
  );
}

/**
 * Product bundle detail page wrapper that loads bundle and tax type data.
 * Renders ProductBundleForm for editing bundle configuration and included products.
 */
export default function ProductBundle(props: ProductBundleProps) {
  const taxTypesQuery = useQuery(TAXTYPES_LIST_FULL_QUERY);
  const bundleQuery = useQuery(PRODUCTBUNDLE_VIEW_QUERY, {
    variables: { id: props.id },
  });

  // Extract spaceId and forLister from bundle data
  const spaceId = bundleQuery.data?.viewProductBundle?.space.id;
  const forLister = bundleQuery.data?.viewProductBundle?.forLister;

  // Only load products once we have spaceId, scoped to the bundle's space
  const productsQuery = useQuery(PRODUCTS_LIST_QUERY, {
    variables: { 
      spaceId,
      where: forLister !== undefined ? { forLister } : undefined
    },
    skip: !spaceId,
  });

  // Decouple loading: page renders when bundle+tax data is ready
  const loading = taxTypesQuery.loading || bundleQuery.loading;
  const error = taxTypesQuery.error || bundleQuery.error;

  // Check for critical data availability
  if (loading) return <CircularProgress />;
  if (error) return <RedirectError err={error} />;
  
  // Defensive: Ensure bundle data exists
  const bundleData = bundleQuery.data?.viewProductBundle;
  const taxTypesData = taxTypesQuery.data?.listTaxTypes;
  
  if (!bundleData || !taxTypesData) {
    return <RedirectError err={new Error('Bundle or tax types data not available')} />;
  }

  // Products error is handled separately - shown as alert in form
  return (
    <>
      {productsQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {i18next.t('productbundle-products-load-error')}: {productsQuery.error.message}
        </Alert>
      )}
      <ProductBundleForm
        data={bundleData as ProductBundleListOutput}
        products={(productsQuery.data?.listProducts.products as ProductListOutput[]) || []}
        productsLoading={productsQuery.loading}
        taxTypesData={taxTypesData as TaxTypeListOutput[]}
      />
    </>
  );
}

export function ProductBundleCreate(props: ProductBundleCreateProps) {
  const filter = filterSelector();
  const user = userSelector()!;
  const bookingMode = bookingModeSelector();

  const taxTypesQuery = useQuery(TAXTYPES_LIST_FULL_QUERY);

  // Determine spaceId and forLister for new bundle
  const spaceId = initialSpace(user, filter)?.id ?? filter?.spaceId;
  const forLister = bookingMode === EBookingMode.EPILO;

  // Only load products once we have spaceId
  const productsQuery = useQuery(PRODUCTS_LIST_QUERY, {
    variables: { 
      spaceId,
      where: { forLister }
    },
    skip: !spaceId,
  });

  // Decouple loading: page renders when tax data is ready
  const loading = taxTypesQuery.loading;
  const error = taxTypesQuery.error;

  // Check for critical data availability
  if (loading) return <CircularProgress />;
  if (error) return <RedirectError err={error} />;

  const taxTypesData = taxTypesQuery.data?.listTaxTypes;
  if (!taxTypesData) {
    return <RedirectError err={new Error('Tax types data not available')} />;
  }

  const defaultTaxType = taxTypesData.find(t => t.lookupCode === 'DEFAULT2');
  if (!defaultTaxType) {
    return <RedirectError err={new Error('Default tax type (DEFAULT2) not found')} />;
  }

  // Products error is handled separately - shown as alert in form
  return (
    <>
      {productsQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {i18next.t('productbundle-products-load-error')}: {productsQuery.error.message}
        </Alert>
      )}
      <ProductBundleForm
        data={{
          id: -1,
          isSystem: false,
          name: '',
          sku: '',
          recurring: EProductPayment.BYGUESTANDDAY,
          forLister: forLister,
          bundleItems: [],
          prices: [],
          content: [],
          components: [
            {
              id: -1,
              taxType: defaultTaxType as TaxTypeListOutput,
            } as ProductBundleComponentListOutput,
          ],
          space: (initialSpace(user, filter) ?? { id: filter?.spaceId }) as any,
        }}
        products={(productsQuery.data?.listProducts.products as ProductListOutput[]) || []}
        productsLoading={productsQuery.loading}
        taxTypesData={taxTypesData as TaxTypeListOutput[]}
      />
    </>
  );
}

interface ProductBundleCopyButtonProps {
  id: number;
  spaceId: number;
  icon: boolean;
}
export function ProductBundleCopyButton(props: ProductBundleCopyButtonProps) {
  const dispatch = useDispatch();

  const user = userSelector()!;
  const canEdit = user.isSeminargo || (user.isAdmin && user.space);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [spaceId, setSpaceId] = useState<number>(props.spaceId);

  const [copyMutateFunction, { loading: copyMutateLoading }] = useMutation(COPY_PRODUCTBUNDLE_MUTATION);

  const __do = async () => {
    if (!canEdit) return;
    try {
      const res = await copyMutateFunction({
        variables: { id: props.id, spaceId: spaceId },
        update: cache => EVICT_PRODUCTBUNDLES_QUERIES(cache),
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_PRODUCTBUNDLES_QUERIES(),
      });
      dispatchMessage(dispatch, i18next.t('productbundle-copied'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  if (props.icon && !canEdit) return null;

  if (props.icon) {
    return (
      <>
        <IconButton disabled={!canEdit || copyMutateLoading} onClick={() => (user.isSingleSpace ? __do() : setDialogOpen(true))}>
          <Tooltip title={i18next.t('productbundle-copy')}>
            <ContentCopyIcon />
          </Tooltip>
        </IconButton>
        <ConfirmationDialog open={dialogOpen} setOpen={setDialogOpen} title={i18next.t('productbundle-confirm-copy-title')} onConfirm={__do}>
          <SimpleDropdown
            name="spaceId"
            onChange={v => setSpaceId(v)}
            options={buildSpaceSelectionOptions(user, true)}
            label={i18next.t('productbundle-copy-space')}
            value={spaceId}
          />
        </ConfirmationDialog>
      </>
    );
  } else {
    return (
      <>
        <Button
          sx={{ marginRight: 2 }}
          variant="contained"
          color="secondary"
          disabled={!canEdit || copyMutateLoading}
          startIcon={copyMutateLoading ? <CircularProgress size={24} /> : <ContentCopyIcon />}
          onClick={() => (user.isSingleSpace ? __do() : setDialogOpen(true))}
        >
          {i18next.t('productbundle-copy')}
        </Button>
        <ConfirmationDialog open={dialogOpen} setOpen={setDialogOpen} title={i18next.t('productbundle-confirm-copy-title')} onConfirm={__do}>
          <SimpleDropdown
            name="spaceId"
            onChange={v => setSpaceId(v)}
            options={buildSpaceSelectionOptions(user, true)}
            label={i18next.t('productbundle-copy-space')}
            value={spaceId}
          />
        </ConfirmationDialog>
      </>
    );
  }
}

interface ProductBundleDeleteButtonProps {
  id: number;
  space: SpaceShortFragment;
  isSystem: boolean;
  forLister: boolean;
  icon: boolean;
}
export function ProductBundleDeleteButton(props: ProductBundleDeleteButtonProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = userSelector()!;
  const canEdit = canEditProductsRecord(user, props);

  const [deleteMutateFunction, { loading: deleteMutateLoading }] = useMutation(DELETE_PRODUCTBUNDLE_MUTATION);

  if (props.icon && !canEdit) return null;

  return (
    <ConfirmationButton
      sx={{ marginRight: 2 }}
      disabled={!canEdit || deleteMutateLoading}
      icon={props.icon}
      {...(props.icon
        ? {}
        : {
          startIcon: deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />,
          variant: 'contained',
          color: 'secondary',
        })}
      confirmationQuestion={i18next.t('productbundle-confirm-delete')}
      confirmationTitle={i18next.t('productbundle-confirm-delete-title')}
      onConfirm={async () => {
        if (!canEdit) return;
        try {
          const res = await deleteMutateFunction({
            variables: {
              id: props.id,
            },
            update: cache => EVICT_PRODUCTBUNDLES_QUERIES(cache),
            awaitRefetchQueries: true,
            refetchQueries: REFETCH_PRODUCTBUNDLES_QUERIES(),
          });
          navigate('/products/bundles');
          dispatchMessage(dispatch, i18next.t('productbundle-deleted'));
        } catch (err) {
          dispatchException(dispatch, err);
        }
      }}
    >
      {props.icon && (deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />)}
      {!props.icon && i18next.t('productbundle-delete')}
    </ConfirmationButton>
  );
}
